import { useEffect } from 'react'
import {
  useMeetingLayoutStore,
  selectMaxTiles,
  visibleTilesPerPage,
  type LayoutMode,
  type TileDensity,
} from '@/features/meeting/store/meeting-layout.store'
import { useResponsiveDevice } from '../hooks/useResponsiveDevice'

export type { LayoutMode, TileDensity }

const MAX_TILES_PER_PAGE = 6
const MAX_TILES_PER_PINNED_PAGE = 6

/**
 * Layouts that put a stage on one side and everyone else in a rail.
 *
 * Both hide you from their own rail while you are the one on the stage, so with
 * nobody else in the room the rail has nothing left to draw.
 */
const RAIL_LAYOUTS: readonly LayoutMode[] = ['sidebar', 'pinned']

/** A rail needs someone other than you in it. `participantsLength` counts you. */
const MIN_PARTICIPANTS_FOR_RAIL = 2

export function useMeetingLayout(participantsLength: number) {
  const storedLayoutMode = useMeetingLayoutStore((s) => s.layoutMode)

  /**
   * The layout actually rendered, which is the stored one except when it cannot
   * work with this many people in the room.
   *
   * Alone in a meeting, `sidebar` and `pinned` both draw you on the stage beside
   * an empty rail — a black column with a drag handle floating in it and no way
   * to tell that anything is meant to be there. Several paths land you in that
   * state without a second person ever being present: choosing Sidebar view in
   * settings, ending a screen share (which restores the layout from before it), or the
   * layout shortcut. Guarding each setter would mean catching all of them and
   * every one added later, so the fallback lives here, where the value is read.
   *
   * Derived rather than written back to the store, so the choice is suspended
   * rather than discarded: the moment someone else joins, the layout the user
   * asked for returns on its own.
   */
  const layoutMode: LayoutMode =
    participantsLength < MIN_PARTICIPANTS_FOR_RAIL && RAIL_LAYOUTS.includes(storedLayoutMode)
      ? 'auto'
      : storedLayoutMode
  const gridPage = useMeetingLayoutStore((s) => s.gridPage)
  const rightRailPage = useMeetingLayoutStore((s) => s.rightRailPage)
  const pinnedPage = useMeetingLayoutStore((s) => s.pinnedPage)
  const pinnedIds = useMeetingLayoutStore((s) => s.pinnedIds)
  const tileDensity = useMeetingLayoutStore((s) => s.tileDensity)
  const hideTilesWithoutVideo = useMeetingLayoutStore((s) => s.hideTilesWithoutVideo)
  const maxTiles = useMeetingLayoutStore(selectMaxTiles)
  const { isMobileDevice, isTabletDevice } = useResponsiveDevice()
  /**
   * Tiles on the page actually drawn: `maxTiles`, capped at six on a phone or
   * tablet — the cap VideoGrid applies. Tiled pages with this, and the
   * active-speaker seating reasons with it, so a phone pages once, the way a
   * desktop does. Paging tiled by `maxTiles` handed VideoGrid sixteen people
   * it then split into pages of six: two sets of page controls, one inside the
   * other. Equal to `maxTiles` on a desktop.
   */
  const tilesPerPage = visibleTilesPerPage(maxTiles, isMobileDevice || isTabletDevice)

  const setLayoutMode = useMeetingLayoutStore((s) => s.setLayoutMode)
  const setGridPage = useMeetingLayoutStore((s) => s.setGridPage)
  const setRightRailPage = useMeetingLayoutStore((s) => s.setRightRailPage)
  const setPinnedPage = useMeetingLayoutStore((s) => s.setPinnedPage)
  const setPinnedIds = useMeetingLayoutStore((s) => s.setPinnedIds)
  const setTileDensity = useMeetingLayoutStore((s) => s.setTileDensity)
  const setHideTilesWithoutVideo = useMeetingLayoutStore((s) => s.setHideTilesWithoutVideo)
  const handlePin = useMeetingLayoutStore((s) => s.handlePin)

  // Pages of `tilesPerPage` — the Settings density, or 16 when it is on Auto,
  // capped on a phone or tablet. The window size does not decide: "Select
  // number of visible videos on Screen" does, and a window-derived count would
  // silently override whatever was chosen there the first time the window was
  // resized.
  const totalPages = Math.max(1, Math.ceil(participantsLength / tilesPerPage))
  const safeGridPage = Math.min(gridPage, totalPages - 1)

  useEffect(() => {
    if (gridPage !== safeGridPage) {
      const timeoutId = setTimeout(() => setGridPage(safeGridPage), 0)
      return () => clearTimeout(timeoutId)
    }
  }, [safeGridPage, gridPage, setGridPage])

  const rightRailPageCount = Math.max(1, Math.ceil(participantsLength / MAX_TILES_PER_PAGE))
  const safeRightRailPage = Math.min(rightRailPage, rightRailPageCount - 1)

  const pinnedPageCount = Math.max(1, Math.ceil(pinnedIds.length / MAX_TILES_PER_PINNED_PAGE))
  const safePinnedPage = Math.min(pinnedPage, pinnedPageCount - 1)

  return {
    layoutMode,
    setLayoutMode,
    gridPage,
    setGridPage,
    safeGridPage,
    rightRailPage,
    setRightRailPage,
    safeRightRailPage,
    rightRailPageCount,
    pinnedPage,
    setPinnedPage,
    safePinnedPage,
    pinnedPageCount,
    pinnedIds,
    setPinnedIds,
    maxTiles,
    tilesPerPage,
    tileDensity,
    setTileDensity,
    hideTilesWithoutVideo,
    setHideTilesWithoutVideo,
    handlePin,
    maxTilesPerPage: MAX_TILES_PER_PAGE,
    maxTilesPerPinnedPage: MAX_TILES_PER_PINNED_PAGE,
  }
}
